import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-administration';

export type AssignableRole = components['schemas']['AssignableRole'];

export type AssignableRolesState =
  | { readonly status: 'loading'; readonly roles: readonly [] }
  | { readonly status: 'ready'; readonly roles: readonly AssignableRole[] }
  | { readonly status: 'error'; readonly roles: readonly [] };

/** The roles this caller may assign at this place: one read, for the hook and for other callers. */
export function readAssignableRoles(placeKind: 'tenants' | 'organizations', placeId: string) {
  return apiClient.GET('/v1/administration/{placeKind}/{placeId}/assignable-roles', {
    params: { path: { placeKind, placeId } },
  });
}

/**
 * The roles this caller may assign at this place (FR-AD-10). The role pickers offer only these, and
 * Utenti's role filter lists them; a role outside the list is never offered, so a refusal
 * (ROLE_NOT_ASSIGNABLE, or PERMISSION_DENIED beyond the caller's reach) is the exception it should
 * be, not a picker's normal answer.
 */
export function useAssignableRoles(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
): { readonly state: AssignableRolesState; readonly retry: () => void } {
  const [state, setState] = useState<AssignableRolesState>({ status: 'loading', roles: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', roles: [] });
    void readAssignableRoles(placeKind, placeId).then(
      ({ data, response }) => {
        if (cancelled) {
          return;
        }
        setState(
          data !== undefined && response.ok
            ? { status: 'ready', roles: data }
            : { status: 'error', roles: [] },
        );
      },
      () => {
        if (!cancelled) {
          setState({ status: 'error', roles: [] });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, retry };
}
